import React, { useState, useEffect, useMemo } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { propertyAPI, rateAPI, seasonAPI, roomAPI } from '@/services/api';
import {
  DollarSign, Plus, Calendar as CalendarIcon, Pencil, Trash2, Users,
  Upload, CheckCircle2, ArrowRight, TrendingUp, TrendingDown, Loader2
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('token');
const authHeaders = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

const OTA_LIST = [
  { key: 'whatsapp', name: 'Venta Directa / WhatsApp', color: 'bg-green-500' },
];

const ADJUST_TYPES = [
  { value: 'increase_fixed', label: '+ Monto fijo', icon: TrendingUp, sign: '+$' },
  { value: 'decrease_fixed', label: '- Monto fijo', icon: TrendingDown, sign: '-$' },
  { value: 'increase_pct', label: '+ Porcentaje', icon: TrendingUp, sign: '+%' },
  { value: 'decrease_pct', label: '- Porcentaje', icon: TrendingDown, sign: '-%' },
];

function calcOtaRate(base, tipo, valor) {
  if (!tipo || !valor) return base;
  const v = parseFloat(valor) || 0;
  switch (tipo) {
    case 'increase_fixed': return base + v;
    case 'decrease_fixed': return Math.max(0, base - v);
    case 'increase_pct': return base * (1 + v / 100);
    case 'decrease_pct': return base * (1 - v / 100);
    default: return base;
  }
}

function calcOccupancyTable(precioBase, tipoAjuste, adicional, ocupacionBase, maxOcupacion) {
  const rows = [];
  for (let p = 1; p <= maxOcupacion; p++) {
    let rate;
    if (p <= ocupacionBase) {
      rate = precioBase;
    } else {
      const extra = p - ocupacionBase;
      if (tipoAjuste === 'increase_pct' || tipoAjuste === 'porcentaje') {
        rate = precioBase;
        for (let i = 0; i < extra; i++) rate = rate * (1 + adicional / 100);
      } else if (tipoAjuste === 'decrease_pct') {
        rate = precioBase;
        for (let i = 0; i < extra; i++) rate = rate * (1 - adicional / 100);
      } else if (tipoAjuste === 'decrease_fixed') {
        rate = precioBase - (extra * adicional);
      } else {
        // increase_fixed or 'monto' (legacy)
        rate = precioBase + (extra * adicional);
      }
    }
    rows.push({ personas: p, tarifa: Math.round(Math.max(0, rate)) });
  }
  return rows;
}

import { useGlobalProperty } from '@/hooks/useGlobalProperty';

export default function RatesPage() {
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useGlobalProperty(properties);
  const [rates, setRates] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rateDialogOpen, setRateDialogOpen] = useState(false);
  const [seasonDialogOpen, setSeasonDialogOpen] = useState(false);
  const [editingRate, setEditingRate] = useState(null);
  const [editingSeason, setEditingSeason] = useState(null);
  const [newRate, setNewRate] = useState({
    nombre: '', precio_base: '', descripcion: '',
    precio_persona_adicional: '', tipo_ajuste_persona: 'monto',
    ocupacion_base: '2', max_ocupacion: '4',
    es_tarifa_madre: true, tarifa_madre_id: '', tipo_ajuste_derivada: '', valor_ajuste_derivada: '',
    linked_room_id: ''
  });
  const [newSeason, setNewSeason] = useState({ nombre: '', fecha_inicio: '', fecha_fin: '', multiplicador: '' });

  // OTA adjustments (now only WhatsApp)
  const [otaAdjustments, setOtaAdjustments] = useState({});
  const [savingOta, setSavingOta] = useState(false);
  const [whatsappRateId, setWhatsappRateId] = useState('');
  const [motorRateId, setMotorRateId] = useState('');

  useEffect(() => {
    propertyAPI.getAll().then(res => {
      setProperties(res.data);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => { if (selectedProperty) fetchData(); }, [selectedProperty]);

  const fetchData = async () => {
    try {
      const [ratesRes, seasonsRes, otaRes, roomsRes] = await Promise.all([
        rateAPI.getAll(selectedProperty),
        seasonAPI.getAll(selectedProperty),
        axios.get(`${BACKEND_URL}/api/ota-adjustments/${selectedProperty}`, authHeaders()).catch(() => ({ data: [] })),
        roomAPI.getAll(selectedProperty).catch(() => ({ data: [] }))
      ]);
      setRates(ratesRes.data);
      setRooms(roomsRes.data || []);
      setSeasons(seasonsRes.data);
      const otaMap = {};
      let linkedRateId = '';
      let motorLinkedRateId = '';
      for (const adj of (otaRes.data || [])) {
        otaMap[adj.ota_name] = { tipo_ajuste: adj.tipo_ajuste, valor: adj.valor?.toString() || '' };
        if (adj.ota_name === 'whatsapp' && adj.rate_id) linkedRateId = adj.rate_id;
        if (adj.ota_name === 'motor' && adj.rate_id) motorLinkedRateId = adj.rate_id;
      }
      setOtaAdjustments(otaMap);
      if (linkedRateId) setWhatsappRateId(linkedRateId);
      else if (ratesRes.data.length > 0) setWhatsappRateId(ratesRes.data[0].id);
      if (motorLinkedRateId) setMotorRateId(motorLinkedRateId);
      else if (ratesRes.data.length > 0) setMotorRateId(ratesRes.data[0].id);
    } catch { toast.error('Error al cargar datos'); }
  };

  const tarifasMadre = rates.filter(r => r.es_tarifa_madre !== false);
  const tarifasDerivadas = rates.filter(r => r.es_tarifa_madre === false);
  const tarifaMadre = tarifasMadre[0];
  const precioBase = tarifaMadre?.precio_base || 0;
  // WhatsApp-linked rate (for adjustment preview)
  const whatsappLinkedRate = rates.find(r => r.id === whatsappRateId) || tarifaMadre;
  const whatsappPrecioBase = whatsappLinkedRate?.precio_base || 0;
  // Motor de Reservas linked rate
  const motorLinkedRate = rates.find(r => r.id === motorRateId) || tarifaMadre;
  const motorPrecioBase = motorLinkedRate?.precio_base || 0;

  // Helper: get parent rate for a derived rate
  const getParentRate = (derivada) => tarifasMadre.find(m => m.id === derivada.tarifa_madre_id);

  // Helper: calculate derived price preview
  const calcDerivedPrice = (parentPrice, tipo, valor) => {
    const v = parseFloat(valor) || 0;
    if (!tipo || !v) return parentPrice;
    switch (tipo) {
      case 'increase_fixed': return parentPrice + v;
      case 'decrease_fixed': return Math.max(0, parentPrice - v);
      case 'increase_pct': return parentPrice * (1 + v / 100);
      case 'decrease_pct': return parentPrice * (1 - v / 100);
      default: return parentPrice;
    }
  };

  // Occupancy preview
  const occupancyTable = useMemo(() => {
    if (!tarifaMadre) return [];
    return calcOccupancyTable(
      tarifaMadre.precio_base,
      tarifaMadre.tipo_ajuste_persona || 'monto',
      tarifaMadre.precio_persona_adicional || 0,
      tarifaMadre.ocupacion_base || 2,
      tarifaMadre.max_ocupacion || 4
    );
  }, [tarifaMadre]);

  // Form occupancy preview
  const formOccTable = useMemo(() => {
    const pb = parseFloat(newRate.precio_base) || 0;
    if (!pb) return [];
    return calcOccupancyTable(
      pb, newRate.tipo_ajuste_persona,
      parseFloat(newRate.precio_persona_adicional) || 0,
      parseInt(newRate.ocupacion_base) || 2,
      parseInt(newRate.max_ocupacion) || 4
    );
  }, [newRate]);

  const handleCreateRate = async (e) => {
    e.preventDefault();
    try {
      const isMadre = newRate.es_tarifa_madre;
      const payload = {
        property_id: selectedProperty,
        nombre: newRate.nombre,
        precio_base: parseFloat(newRate.precio_base) || 0,
        descripcion: newRate.descripcion || '',
        precio_persona_adicional: parseFloat(newRate.precio_persona_adicional) || 0,
        tipo_ajuste_persona: newRate.tipo_ajuste_persona || 'monto',
        ocupacion_base: parseInt(newRate.ocupacion_base) || 2,
        max_ocupacion: parseInt(newRate.max_ocupacion) || 4,
        es_tarifa_madre: isMadre,
        tarifa_madre_id: isMadre ? null : (newRate.tarifa_madre_id || null),
        tipo_ajuste_derivada: isMadre ? null : (newRate.tipo_ajuste_derivada || null),
        valor_ajuste_derivada: isMadre ? 0 : (parseFloat(newRate.valor_ajuste_derivada) || 0),
        linked_room_id: isMadre ? null : (newRate.linked_room_id || null),
      };
      if (editingRate) {
        await axios.put(`${BACKEND_URL}/api/rates/${editingRate.id}`, payload, authHeaders());
      } else {
        await rateAPI.create(payload);
      }
      toast.success('Tarifa guardada');
      setRateDialogOpen(false);
      setEditingRate(null);
      resetRateForm();
      fetchData();
    } catch { toast.error('Error al guardar tarifa'); }
  };

  const resetRateForm = () => {
    setNewRate({ nombre: '', precio_base: '', descripcion: '', precio_persona_adicional: '', tipo_ajuste_persona: 'monto', ocupacion_base: '2', max_ocupacion: '4', es_tarifa_madre: true, tarifa_madre_id: '', tipo_ajuste_derivada: '', valor_ajuste_derivada: '', linked_room_id: '' });
  };

  const handleEditRate = (rate) => {
    setEditingRate(rate);
    setNewRate({
      nombre: rate.nombre,
      precio_base: rate.precio_base.toString(),
      descripcion: rate.descripcion || '',
      precio_persona_adicional: (rate.precio_persona_adicional || 0).toString(),
      tipo_ajuste_persona: rate.tipo_ajuste_persona || 'monto',
      ocupacion_base: (rate.ocupacion_base || 2).toString(),
      max_ocupacion: (rate.max_ocupacion || 4).toString(),
      es_tarifa_madre: rate.es_tarifa_madre !== false,
      tarifa_madre_id: rate.tarifa_madre_id || '',
      tipo_ajuste_derivada: rate.tipo_ajuste_derivada || '',
      valor_ajuste_derivada: (rate.valor_ajuste_derivada || 0).toString(),
      linked_room_id: rate.linked_room_id || ''
    });
    setRateDialogOpen(true);
  };

  // Delete confirmation state for rates
  const [deleteRateTarget, setDeleteRateTarget] = useState(null);
  const [deletingRate, setDeletingRate] = useState(false);

  const handleDeleteRate = (rateId) => {
    const rate = rates.find(r => r.id === rateId);
    const isMadre = rate?.es_tarifa_madre !== false;
    const childCount = isMadre ? rates.filter(r => r.tarifa_madre_id === rateId).length : 0;
    setDeleteRateTarget({ id: rateId, nombre: rate?.nombre || '', isMadre, childCount });
  };

  const confirmDeleteRate = async () => {
    if (!deleteRateTarget) return;
    setDeletingRate(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/rates/${deleteRateTarget.id}`, authHeaders());
      toast.success(`Tarifa "${deleteRateTarget.nombre}" eliminada`);
      fetchData();
    } catch { toast.error('Error al eliminar'); }
    finally { setDeletingRate(false); setDeleteRateTarget(null); }
  };

  const handleCreateSeason = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...newSeason, property_id: selectedProperty, multiplicador: parseFloat(newSeason.multiplicador), activo: true };
      if (editingSeason) {
        await axios.put(`${BACKEND_URL}/api/seasons/${editingSeason.id}`, payload, authHeaders());
      } else {
        await seasonAPI.create(payload);
      }
      toast.success('Temporada guardada — sincronizando con Channex...');
      setSeasonDialogOpen(false);
      setEditingSeason(null);
      setNewSeason({ nombre: '', fecha_inicio: '', fecha_fin: '', multiplicador: '' });
      fetchData();
    } catch { toast.error('Error al guardar temporada'); }
  };

  const handleEditSeason = (season) => {
    setEditingSeason(season);
    setNewSeason({ nombre: season.nombre, fecha_inicio: season.fecha_inicio, fecha_fin: season.fecha_fin, multiplicador: season.multiplicador.toString() });
    setSeasonDialogOpen(true);
  };

  const handleDeleteSeason = async (seasonId) => {
    if (!window.confirm('¿Eliminar esta temporada?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/seasons/${seasonId}`, authHeaders());
      toast.success('Temporada eliminada');
      fetchData();
    } catch { toast.error('Error al eliminar'); }
  };

  const saveOtaAdjustments = async () => {
    setSavingOta(true);
    try {
      const adjustments = [];
      // WhatsApp adjustment
      const waAdj = otaAdjustments['whatsapp'];
      if (waAdj?.tipo_ajuste && waAdj?.valor) {
        adjustments.push({ ota_name: 'whatsapp', tipo_ajuste: waAdj.tipo_ajuste, valor: parseFloat(waAdj.valor) || 0, rate_id: whatsappRateId });
      }
      // Motor de Reservas adjustment
      const motorAdj = otaAdjustments['motor'];
      if (motorAdj?.tipo_ajuste && motorAdj?.valor) {
        adjustments.push({ ota_name: 'motor', tipo_ajuste: motorAdj.tipo_ajuste, valor: parseFloat(motorAdj.valor) || 0, rate_id: motorRateId });
      }
      await axios.post(`${BACKEND_URL}/api/ota-adjustments`, { property_id: selectedProperty, adjustments }, authHeaders());
      toast.success('Ajustes guardados');
    } catch { toast.error('Error al guardar'); }
    finally { setSavingOta(false); }
  };

  const updateOta = (ota, field, value) => {
    setOtaAdjustments(prev => ({
      ...prev,
      [ota]: { ...(prev[ota] || {}), [field]: value }
    }));
  };

  const fmt = (n) => `$${Math.round(n).toLocaleString('es-CO')}`;

  if (loading) {
    return <DashboardLayout><div className="flex items-center justify-center h-96"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div></DashboardLayout>;
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="rates-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'Manrope' }}>Sistema de Tarifas</h1>
            <p className="text-muted-foreground mt-1">Tarifa madre, ocupación y ajuste para WhatsApp</p>
          </div>
          {properties.length > 0 && (
            <Select value={selectedProperty} onValueChange={setSelectedProperty}>
              <SelectTrigger className="w-[280px]" data-testid="rates-property-selector">
                <SelectValue placeholder="Propiedad" />
              </SelectTrigger>
              <SelectContent>
                {properties.map(p => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* ========== 1. TARIFAS JERÁRQUICAS ========== */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle style={{ fontFamily: 'Manrope' }}>Tarifas</CardTitle>
              <CardDescription>Crea tarifas madre y tarifas derivadas vinculadas</CardDescription>
            </div>
            <div className="flex gap-2">
              <Dialog open={rateDialogOpen} onOpenChange={(open) => { setRateDialogOpen(open); if (!open) { setEditingRate(null); resetRateForm(); } }}>
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline" onClick={() => { resetRateForm(); setNewRate(prev => ({ ...prev, es_tarifa_madre: true })); }} data-testid="add-madre-btn">
                    <Plus className="h-4 w-4 mr-1" /> Tarifa Madre
                  </Button>
                </DialogTrigger>
                {tarifasMadre.length > 0 && (
                  <DialogTrigger asChild>
                    <Button size="sm" onClick={() => { resetRateForm(); setNewRate(prev => ({ ...prev, es_tarifa_madre: false, tarifa_madre_id: tarifasMadre[0]?.id || '' })); }} data-testid="add-derivada-btn">
                      <Plus className="h-4 w-4 mr-1" /> Tarifa Derivada
                    </Button>
                  </DialogTrigger>
                )}
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>{editingRate ? 'Editar Tarifa' : (newRate.es_tarifa_madre ? 'Nueva Tarifa Madre' : 'Nueva Tarifa Derivada')}</DialogTitle>
                    <DialogDescription>
                      {newRate.es_tarifa_madre 
                        ? 'Define una tarifa base con su precio y ocupación' 
                        : 'Crea una tarifa vinculada a una tarifa madre existente'}
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateRate} className="space-y-4">
                    <div className="space-y-1">
                      <Label className="text-xs">Nombre de la tarifa</Label>
                      <Input value={newRate.nombre} onChange={e => setNewRate({ ...newRate, nombre: e.target.value })} placeholder={newRate.es_tarifa_madre ? 'Ej: Apartamento Luxury' : 'Ej: Tarifa Booking'} required data-testid="rate-nombre-input" />
                    </div>

                    {/* Derived rate: select parent */}
                    {!newRate.es_tarifa_madre && (
                      <>
                        <Separator />
                        <Label className="text-sm font-semibold">Vincular a Tarifa Madre</Label>
                        <Select value={newRate.tarifa_madre_id} onValueChange={v => setNewRate({ ...newRate, tarifa_madre_id: v })} required>
                          <SelectTrigger data-testid="rate-madre-select">
                            <SelectValue placeholder="Seleccionar tarifa madre..." />
                          </SelectTrigger>
                          <SelectContent>
                            {tarifasMadre.map(m => (
                              <SelectItem key={m.id} value={m.id}>{m.nombre} — {fmt(m.precio_base)}/noche</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        {/* Link to room */}
                        <div className="space-y-1">
                          <Label className="text-xs">Habitación / Apartamento vinculado</Label>
                          <Select value={newRate.linked_room_id} onValueChange={v => setNewRate({ ...newRate, linked_room_id: v })}>
                            <SelectTrigger data-testid="rate-linked-room">
                              <SelectValue placeholder="Seleccionar habitación..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Sin vincular</SelectItem>
                              {rooms.map(r => (
                                <SelectItem key={r.id} value={r.id}>{r.nombre} {r.tipo ? `(${r.tipo})` : ''}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs">Tipo de ajuste</Label>
                            <Select value={newRate.tipo_ajuste_derivada} onValueChange={v => setNewRate({ ...newRate, tipo_ajuste_derivada: v })}>
                              <SelectTrigger data-testid="rate-derivada-tipo">
                                <SelectValue placeholder="Seleccionar..." />
                              </SelectTrigger>
                              <SelectContent>
                                {ADJUST_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Valor {newRate.tipo_ajuste_derivada?.includes('pct') ? '(%)' : '($)'}</Label>
                            <Input type="number" min="0" value={newRate.valor_ajuste_derivada} onChange={e => setNewRate({ ...newRate, valor_ajuste_derivada: e.target.value })} placeholder={newRate.tipo_ajuste_derivada?.includes('pct') ? '10' : '50000'} data-testid="rate-derivada-valor" />
                          </div>
                        </div>
                        {/* Preview derived price */}
                        {newRate.tarifa_madre_id && newRate.tipo_ajuste_derivada && newRate.valor_ajuste_derivada && (() => {
                          const parent = tarifasMadre.find(m => m.id === newRate.tarifa_madre_id);
                          if (!parent) return null;
                          const finalPrice = calcDerivedPrice(parent.precio_base, newRate.tipo_ajuste_derivada, newRate.valor_ajuste_derivada);
                          return (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                              <div className="text-xs text-blue-600">Precio base calculado</div>
                              <div className="text-2xl font-bold text-blue-800" style={{ fontFamily: 'Manrope' }}>{fmt(finalPrice)}</div>
                              <div className="text-xs text-muted-foreground">base {fmt(parent.precio_base)} {newRate.tipo_ajuste_derivada.includes('increase') ? '+' : '-'} {newRate.tipo_ajuste_derivada.includes('pct') ? `${newRate.valor_ajuste_derivada}%` : fmt(parseFloat(newRate.valor_ajuste_derivada) || 0)}</div>
                            </div>
                          );
                        })()}

                        {/* Occupancy / Extra person — same as madre */}
                        <Separator />
                        <Label className="text-sm font-semibold">Ajuste por persona adicional</Label>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs">Ocupación base</Label>
                            <Input type="number" min="1" max="10" value={newRate.ocupacion_base} onChange={e => setNewRate({ ...newRate, ocupacion_base: e.target.value })} data-testid="rate-derivada-ocupacion" />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Máx. ocupación</Label>
                            <Input type="number" min="1" max="10" value={newRate.max_ocupacion} onChange={e => setNewRate({ ...newRate, max_ocupacion: e.target.value })} data-testid="rate-derivada-max-occ" />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs">Tipo de ajuste por persona</Label>
                            <Select value={newRate.tipo_ajuste_persona} onValueChange={v => setNewRate({ ...newRate, tipo_ajuste_persona: v })}>
                              <SelectTrigger data-testid="rate-derivada-tipo-persona"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="increase_fixed">+ Monto fijo ($)</SelectItem>
                                <SelectItem value="decrease_fixed">- Monto fijo ($)</SelectItem>
                                <SelectItem value="increase_pct">+ Porcentaje (%)</SelectItem>
                                <SelectItem value="decrease_pct">- Porcentaje (%)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Valor {newRate.tipo_ajuste_persona?.includes('pct') ? '(%)' : '($)'}</Label>
                            <Input type="number" min="0" value={newRate.precio_persona_adicional} onChange={e => setNewRate({ ...newRate, precio_persona_adicional: e.target.value })} placeholder={newRate.tipo_ajuste_persona?.includes('pct') ? '20' : '30000'} data-testid="rate-derivada-persona-adicional" />
                          </div>
                        </div>
                      </>
                    )}

                    {/* Mother rate: price + occupancy */}
                    {newRate.es_tarifa_madre && (
                      <>
                        <div className="space-y-1">
                          <Label className="text-xs">Precio Base (COP/noche)</Label>
                          <Input type="number" min="0" value={newRate.precio_base} onChange={e => setNewRate({ ...newRate, precio_base: e.target.value })} placeholder="150000" required data-testid="rate-precio-input" />
                        </div>
                        <Separator />
                        <Label className="text-sm font-semibold">Ajuste por persona adicional</Label>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs">Ocupación base</Label>
                            <Input type="number" min="1" max="10" value={newRate.ocupacion_base} onChange={e => setNewRate({ ...newRate, ocupacion_base: e.target.value })} data-testid="rate-ocupacion-input" />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Máx. ocupación</Label>
                            <Input type="number" min="1" max="10" value={newRate.max_ocupacion} onChange={e => setNewRate({ ...newRate, max_ocupacion: e.target.value })} data-testid="rate-max-occ-input" />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs">Tipo de ajuste por persona</Label>
                            <Select value={newRate.tipo_ajuste_persona} onValueChange={v => setNewRate({ ...newRate, tipo_ajuste_persona: v })}>
                              <SelectTrigger data-testid="rate-tipo-ajuste-select"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="increase_fixed">+ Monto fijo ($)</SelectItem>
                                <SelectItem value="decrease_fixed">- Monto fijo ($)</SelectItem>
                                <SelectItem value="increase_pct">+ Porcentaje (%)</SelectItem>
                                <SelectItem value="decrease_pct">- Porcentaje (%)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Valor {newRate.tipo_ajuste_persona?.includes('pct') ? '(%)' : '($)'}</Label>
                            <Input type="number" min="0" value={newRate.precio_persona_adicional} onChange={e => setNewRate({ ...newRate, precio_persona_adicional: e.target.value })} placeholder={newRate.tipo_ajuste_persona?.includes('pct') ? '20' : '30000'} data-testid="rate-persona-adicional-input" />
                          </div>
                        </div>
                        {formOccTable.length > 0 && (
                          <div className="bg-muted/50 rounded-lg p-3">
                            <p className="text-xs font-semibold text-muted-foreground mb-2">Vista previa por ocupación:</p>
                            <div className="grid grid-cols-2 gap-1 text-xs">
                              {formOccTable.map(r => (
                                <div key={r.personas} className="flex justify-between px-2 py-1 rounded bg-white">
                                  <span>{r.personas} persona{r.personas > 1 ? 's' : ''}</span>
                                  <span className="font-bold">{fmt(r.tarifa)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    <div className="space-y-1">
                      <Label className="text-xs">Descripción (opcional)</Label>
                      <Input value={newRate.descripcion} onChange={e => setNewRate({ ...newRate, descripcion: e.target.value })} placeholder="Descripción de la tarifa" data-testid="rate-descripcion-input" />
                    </div>
                    <Button type="submit" className="w-full" data-testid="rate-submit-btn">
                      {editingRate ? 'Guardar Cambios' : (newRate.es_tarifa_madre ? 'Crear Tarifa Madre' : 'Crear Tarifa Derivada')}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            {rates.length === 0 ? (
              <div className="text-center py-8">
                <DollarSign className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No hay tarifas configuradas. Crea tu primera Tarifa Madre.</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Render each Tarifa Madre with its derived rates */}
                {tarifasMadre.map((madre) => {
                  const hijas = tarifasDerivadas.filter(d => d.tarifa_madre_id === madre.id);
                  return (
                    <div key={madre.id} className="space-y-2">
                      {/* Tarifa Madre card */}
                      <div className="p-4 border-2 border-primary/40 bg-primary/5 rounded-xl" data-testid={`rate-madre-${madre.id}`}>
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-lg" style={{ fontFamily: 'Manrope' }}>{madre.nombre}</h3>
                              <Badge className="bg-primary text-primary-foreground text-[10px]">TARIFA MADRE</Badge>
                            </div>
                            {madre.descripcion && <p className="text-sm text-muted-foreground mt-1">{madre.descripcion}</p>}
                            <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                              <span><Users className="h-3 w-3 inline mr-1" />Base: {madre.ocupacion_base || 2}p</span>
                              <span>Máx: {madre.max_ocupacion || 4}p</span>
                              <span>+{madre.tipo_ajuste_persona === 'porcentaje' ? `${madre.precio_persona_adicional || 0}%` : fmt(madre.precio_persona_adicional || 0)} /persona extra</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right">
                              <div className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>{fmt(madre.precio_base)}</div>
                              <div className="text-xs text-muted-foreground">por noche</div>
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => handleEditRate(madre)}><Pencil className="h-4 w-4" /></Button>
                            <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeleteRate(madre.id)}><Trash2 className="h-4 w-4" /></Button>
                          </div>
                        </div>
                      </div>

                      {/* Derived rates */}
                      {hijas.map((hija) => (
                        <div key={hija.id} className="ml-8 p-3 border border-blue-200 bg-blue-50/50 rounded-lg flex items-center justify-between" data-testid={`rate-derivada-${hija.id}`}>
                          <div className="flex items-center gap-3">
                            <div className="w-0.5 h-8 bg-primary/30 rounded-full" />
                            <div>
                              <div className="flex items-center gap-2">
                                <h4 className="font-medium text-sm">{hija.nombre}</h4>
                                <Badge variant="outline" className="text-[10px] border-blue-300 text-blue-600">DERIVADA</Badge>
                              </div>
                              <div className="text-xs text-muted-foreground mt-0.5">
                                {hija.tipo_ajuste_derivada?.includes('increase') ? '+' : '-'}
                                {hija.tipo_ajuste_derivada?.includes('pct') 
                                  ? `${hija.valor_ajuste_derivada}%`
                                  : fmt(hija.valor_ajuste_derivada || 0)
                                } sobre {madre.nombre}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right">
                              <div className="text-lg font-bold text-blue-700" style={{ fontFamily: 'Manrope' }}>{fmt(hija.precio_base)}</div>
                              <div className="text-[10px] text-muted-foreground">por noche</div>
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => handleEditRate(hija)}><Pencil className="h-3.5 w-3.5" /></Button>
                            <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeleteRate(hija.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}

                {/* Orphan derived rates (parent was deleted) */}
                {tarifasDerivadas.filter(d => !tarifasMadre.find(m => m.id === d.tarifa_madre_id)).map((orphan) => (
                  <div key={orphan.id} className="p-3 border border-amber-200 bg-amber-50/50 rounded-lg flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-sm">{orphan.nombre}</h4>
                        <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-600">SIN MADRE</Badge>
                      </div>
                      <div className="text-xs text-muted-foreground">{fmt(orphan.precio_base)}/noche</div>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleEditRate(orphan)}><Pencil className="h-3.5 w-3.5" /></Button>
                      <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeleteRate(orphan.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* ========== 2. AJUSTES POR CANAL ========== */}
        {rates.length > 0 && (
          <Card data-testid="ota-adjustments-card">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Manrope' }}>Ajustes por Canal de Venta</CardTitle>
              <CardDescription>Configura los precios para WhatsApp y el Motor de Reservas. Los canales OTA se gestionan desde Channex.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* --- WhatsApp --- */}
              {(() => {
                const adj = otaAdjustments['whatsapp'] || {};
                const finalRate = calcOtaRate(whatsappPrecioBase, adj.tipo_ajuste, adj.valor);
                return (
                  <div className="border rounded-xl p-5 space-y-4" data-testid="ota-card-whatsapp">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-green-500" />
                      <span className="font-semibold">Venta Directa / WhatsApp</span>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-medium">Tarifa madre vinculada</Label>
                      <Select value={whatsappRateId} onValueChange={setWhatsappRateId}>
                        <SelectTrigger className="max-w-sm" data-testid="whatsapp-rate-select"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                        <SelectContent>{rates.map(r => <SelectItem key={r.id} value={r.id}>{r.nombre} — {fmt(r.precio_base)}/noche</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="grid sm:grid-cols-3 gap-3 items-end">
                      <div className="space-y-1">
                        <Label className="text-xs">Tipo de ajuste</Label>
                        <Select value={adj.tipo_ajuste || ''} onValueChange={v => updateOta('whatsapp', 'tipo_ajuste', v)}>
                          <SelectTrigger data-testid="ota-tipo-whatsapp"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                          <SelectContent>{ADJUST_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Valor {adj.tipo_ajuste?.includes('pct') ? '(%)' : '($)'}</Label>
                        <Input type="number" min="0" value={adj.valor || ''} onChange={e => updateOta('whatsapp', 'valor', e.target.value)} data-testid="ota-valor-whatsapp" />
                      </div>
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                        <div className="text-[10px] text-muted-foreground">Tarifa WhatsApp</div>
                        <div className="font-bold text-xl text-green-700" style={{ fontFamily: 'Manrope' }}>{fmt(finalRate)}</div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* --- Motor de Reservas --- */}
              {(() => {
                const adj = otaAdjustments['motor'] || {};
                const finalRate = calcOtaRate(motorPrecioBase, adj.tipo_ajuste, adj.valor);
                const selProp = properties.find(p => p.id === selectedProperty);
                const bookingUrl = selProp?.codigo_unico ? `${window.location.origin}/reservar/${selProp.codigo_unico}` : '';
                return (
                  <div className="border rounded-xl p-5 space-y-4 border-blue-200 bg-blue-50/30" data-testid="ota-card-motor">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-blue-500" />
                        <span className="font-semibold">Motor de Reservas</span>
                        <Badge variant="outline" className="text-[10px] border-blue-300 text-blue-600">Público</Badge>
                      </div>
                      {bookingUrl && (
                        <button onClick={() => { navigator.clipboard.writeText(bookingUrl); toast.success('Link copiado'); }} className="text-xs text-blue-600 hover:underline" data-testid="copy-booking-link">
                          Copiar link
                        </button>
                      )}
                    </div>
                    {bookingUrl && (
                      <div className="bg-white border rounded-lg px-3 py-2 text-xs font-mono text-slate-600 truncate" data-testid="booking-link-display">{bookingUrl}</div>
                    )}
                    <div className="space-y-2">
                      <Label className="text-xs font-medium">Tarifa madre vinculada</Label>
                      <Select value={motorRateId} onValueChange={setMotorRateId}>
                        <SelectTrigger className="max-w-sm" data-testid="motor-rate-select"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                        <SelectContent>{rates.map(r => <SelectItem key={r.id} value={r.id}>{r.nombre} — {fmt(r.precio_base)}/noche</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="grid sm:grid-cols-3 gap-3 items-end">
                      <div className="space-y-1">
                        <Label className="text-xs">Tipo de ajuste</Label>
                        <Select value={adj.tipo_ajuste || ''} onValueChange={v => updateOta('motor', 'tipo_ajuste', v)}>
                          <SelectTrigger data-testid="ota-tipo-motor"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                          <SelectContent>{ADJUST_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Valor {adj.tipo_ajuste?.includes('pct') ? '(%)' : '($)'}</Label>
                        <Input type="number" min="0" value={adj.valor || ''} onChange={e => updateOta('motor', 'valor', e.target.value)} data-testid="ota-valor-motor" />
                      </div>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                        <div className="text-[10px] text-muted-foreground">Tarifa Motor</div>
                        <div className="font-bold text-xl text-blue-700" style={{ fontFamily: 'Manrope' }}>{fmt(finalRate)}</div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              <Button onClick={saveOtaAdjustments} disabled={savingOta} className="w-full" data-testid="btn-save-ota-adjustments">
                {savingOta ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Upload className="h-4 w-4 mr-1" />}
                Guardar Ajustes
              </Button>
            </CardContent>
          </Card>
        )}

        {/* ========== 3. FLOW VISUALIZATION ========== */}
        {whatsappPrecioBase > 0 && (
          <Card className="bg-gradient-to-r from-primary/5 to-green-50 border-primary/20" data-testid="flow-card">
            <CardContent className="pt-6">
              <p className="text-xs font-semibold text-muted-foreground mb-3">FLUJO DE PRECIOS WHATSAPP</p>
              <div className="flex items-center gap-3 flex-wrap text-sm">
                <div className="bg-white rounded-lg p-3 border shadow-sm text-center">
                  <div className="text-[10px] text-muted-foreground">Tarifa Madre</div>
                  <div className="font-bold text-lg">{fmt(whatsappPrecioBase)}</div>
                  <div className="text-[10px] text-muted-foreground">{whatsappLinkedRate?.nombre}</div>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
                {(() => {
                  const adj = otaAdjustments['whatsapp'] || {};
                  const final_r = calcOtaRate(whatsappPrecioBase, adj.tipo_ajuste, adj.valor);
                  return (
                    <div className="bg-white rounded-lg p-3 border-2 border-green-300 shadow-sm text-center min-w-[120px]">
                      <div className="w-3 h-3 rounded-full bg-green-500 mx-auto mb-1" />
                      <div className="text-[10px] text-muted-foreground">WhatsApp</div>
                      <div className="font-bold text-lg text-green-700">{fmt(final_r)}</div>
                    </div>
                  );
                })()}
              </div>
              <p className="text-xs text-muted-foreground mt-3">Los canales Booking, Airbnb y Expedia se gestionan directamente desde los ajustes de Channex.</p>
            </CardContent>
          </Card>
        )}

        {/* ========== 4. TEMPORADAS ========== */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle style={{ fontFamily: 'Manrope' }}>Temporadas</CardTitle>
              <CardDescription>Multiplicadores automáticos que afectan la tarifa madre</CardDescription>
            </div>
            <Dialog open={seasonDialogOpen} onOpenChange={(open) => { setSeasonDialogOpen(open); if (!open) setEditingSeason(null); }}>
              <DialogTrigger asChild>
                <Button size="sm" onClick={() => setNewSeason({ nombre: '', fecha_inicio: '', fecha_fin: '', multiplicador: '' })} data-testid="add-season-btn">
                  <Plus className="h-4 w-4 mr-2" /> Agregar Temporada
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingSeason ? 'Editar Temporada' : 'Nueva Temporada'}</DialogTitle>
                  <DialogDescription>Precios ajustados por periodo. Aplican multiplicador sobre la tarifa madre.</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateSeason} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Nombre</Label>
                    <Input value={newSeason.nombre} onChange={e => setNewSeason({ ...newSeason, nombre: e.target.value })} placeholder="Temporada Alta Navidad" required data-testid="season-nombre-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Fecha Inicio</Label>
                      <Input type="date" value={newSeason.fecha_inicio} onChange={e => setNewSeason({ ...newSeason, fecha_inicio: e.target.value })} required data-testid="season-inicio-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Fecha Fin</Label>
                      <Input type="date" value={newSeason.fecha_fin} onChange={e => setNewSeason({ ...newSeason, fecha_fin: e.target.value })} required data-testid="season-fin-input" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Multiplicador</Label>
                    <Input type="number" min="0.1" step="0.1" value={newSeason.multiplicador} onChange={e => setNewSeason({ ...newSeason, multiplicador: e.target.value })} placeholder="1.5" required data-testid="season-multiplicador-input" />
                    <p className="text-xs text-muted-foreground">1.5 = +50%, 0.8 = -20%. Madre {fmt(precioBase)} × 1.5 = {fmt(precioBase * 1.5)}</p>
                  </div>
                  <Button type="submit" className="w-full" data-testid="season-submit-btn">
                    {editingSeason ? 'Guardar Cambios' : 'Crear Temporada'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            {seasons.length === 0 ? (
              <div className="text-center py-8">
                <CalendarIcon className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No hay temporadas configuradas</p>
              </div>
            ) : (
              <div className="space-y-3">
                {seasons.map(season => (
                  <div key={season.id} className="p-4 border rounded-lg">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold">{season.nombre}</h3>
                        <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                          <span>
                            <CalendarIcon className="h-3 w-3 inline mr-1" />
                            {format(parseISO(season.fecha_inicio), 'dd MMM yyyy', { locale: es })} - {format(parseISO(season.fecha_fin), 'dd MMM yyyy', { locale: es })}
                          </span>
                          {precioBase > 0 && <span className="font-medium text-primary">{fmt(precioBase)} × {season.multiplicador} = {fmt(precioBase * season.multiplicador)}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={season.multiplicador > 1 ? 'default' : 'secondary'}>
                          {season.multiplicador > 1 ? '+' : ''}{((season.multiplicador - 1) * 100).toFixed(0)}%
                        </Badge>
                        <Button variant="ghost" size="sm" onClick={() => handleEditSeason(season)}><Pencil className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeleteSeason(season.id)}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Delete Rate Confirmation Dialog */}
      <AlertDialog open={!!deleteRateTarget} onOpenChange={(open) => !open && setDeleteRateTarget(null)}>
        <AlertDialogContent data-testid="delete-rate-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar tarifa</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro de eliminar la tarifa <strong>"{deleteRateTarget?.nombre}"</strong>?
              {deleteRateTarget?.isMadre && deleteRateTarget?.childCount > 0 && (
                <span className="block mt-2 text-amber-600 font-medium">
                  Esta tarifa madre tiene {deleteRateTarget.childCount} tarifa(s) derivada(s) que quedarán sin vínculo.
                </span>
              )}
              <span className="block mt-1">Esta acción no se puede deshacer.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingRate} data-testid="delete-rate-cancel">No, cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteRate} disabled={deletingRate} className="bg-red-600 hover:bg-red-700" data-testid="delete-rate-confirm">
              {deletingRate ? 'Eliminando...' : 'Sí, eliminar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
